import React, { useState } from 'react';
import Select from 'react-select';
import { Plus, Calendar, Package, FileText, Save, Trash2, RefreshCw, FileStack, AlertCircle, CheckCircle, X } from 'lucide-react';
import { User, Module } from '../../types';
import { getApiUrl } from '../../config/api';
import InstituteSelectionModal from './InstituteSelectionModal';
import ModuleSelectionModal from './ModuleSelectionModal';
import { ConflictCheck } from './ConflictCheck';
import { todayLocal } from '../../utils/localDate';
// import { mockModules } from '../../data/mockData';

interface SubmitRequestFormProps {
  currentUser: User;
  // Must reject/throw on failure so the form keeps what the user entered.
  onSubmit: (request: any) => Promise<void> | void;
}

const SubmitRequestForm: React.FC<SubmitRequestFormProps> = ({ currentUser, onSubmit }) => {
  const [modules, setModules] = useState<Module[]>([]);
  const [modulesLoading, setModulesLoading] = useState(true);
  // Fetch modules and institutes from backend
  const [institutes, setInstitutes] = useState<any[]>([]);
  const [institutesLoading, setInstitutesLoading] = useState(true);
  const [tls, setTls] = useState<User[]>([]);
  const [pms, setPms] = useState<User[]>([]);
  const [devopsUsers, setDevopsUsers] = useState<User[]>([]);
  const [testers, setTesters] = useState<User[]>([]); // Add testers state
  const [dbScriptFiles, setDbScriptFiles] = useState<Array<{name: string, content: string}>>([]);
  // Optional, manually-entered branch name per selected module (moduleId -> branch)
  const [moduleBranches, setModuleBranches] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  React.useEffect(() => {
    const fetchModules = async () => {
      try {
        const res = await fetch(getApiUrl('api/modules'));
        if (!res.ok) throw new Error('Failed to fetch modules');
        const data = await res.json();
        setModules(data);
      } catch (err) {
        console.error('Error fetching modules:', err);
        setModules([]);
      } finally {
        setModulesLoading(false);
      }
    };
    const fetchInstitutes = async () => {
      try {
        const res = await fetch(getApiUrl('api/institutes'));
        if (!res.ok) throw new Error('Failed to fetch institutes');
        const data = await res.json();
        setInstitutes(data);
      } catch (err) {
        console.error('Error fetching institutes:', err);
        setInstitutes([]);
      } finally {
        setInstitutesLoading(false);
      }
    };
    const fetchUsers = async () => {
      try {
        const res = await fetch(getApiUrl('api/users'));
        if (!res.ok) throw new Error('Failed to fetch users');
        const data = await res.json();
        // Filter by realmRoles or roles array
        const tlsList = data.filter((u: any) => {
          const roles = u.realmRoles || u.roles || [];
          return roles.some((r: string) => r.toLowerCase() === 'tl') || u.role === 'tl';
        });
        const pmsList = data.filter((u: any) => {
          const roles = u.realmRoles || u.roles || [];
          return roles.some((r: string) => r.toLowerCase() === 'pm') || u.role === 'pm';
        });
        const devopsList = data.filter((u: any) => {
          const roles = u.realmRoles || u.roles || [];
          return roles.some((r: string) => r.toLowerCase() === 'devops') || u.role === 'devops';
        });
        setTls(tlsList);
        setPms(pmsList);
        setDevopsUsers(devopsList);
        
        // Auto-select all DevOps users
        const devopsIds = devopsList.map((u: any) => u.id);
        setFormData(prev => ({ ...prev, devopsAssignees: devopsIds }));
      } catch (err) {
        console.error('Error fetching users:', err);
        setTls([]);
        setPms([]);
      }
    };
    const fetchTesters = async () => {
      try {
        const res = await fetch(getApiUrl('api/testers'));
        if (!res.ok) throw new Error('Failed to fetch testers');
        const data = await res.json();
        setTesters(data);
      } catch (err) {
        console.error('Error fetching testers:', err);
        setTesters([]);
      }
    };
    fetchModules();
    fetchInstitutes();
    fetchUsers();
    fetchTesters();
  }, []);
  const [showInstituteModal, setShowInstituteModal] = useState(false);
  const [showModuleModal, setShowModuleModal] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium' as 'low' | 'medium' | 'high',
    deploymentType: 'feature' as 'bug' | 'feature' | 'payload' | 'version' | 'new-deployment' | 'db-script',
    modules: [] as string[],
    institutes: [] as string[],
    tls: [] as string[],
    pms: [] as string[],
    devopsAssignees: [] as string[],
    scheduledDate: '', // Will be set by datetime-local input
    dbScript: '', // Optional DB script
    dbScriptFileName: '', // Optional DB script filename
    testers: [] as string[], // Optional testers
    notifyTeam: true, // Notify other developers in same institute/module
  });
  const [requestType, setRequestType] = useState('tl_to_pm');
  const [savedDrafts, setSavedDrafts] = useState<any[]>([]);
  const [showDrafts, setShowDrafts] = useState(false);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState<{
    type: 'confirm' | 'alert' | 'prompt';
    title: string;
    message: string;
    onConfirm?: () => void;
    onCancel?: () => void;
    confirmText?: string;
    cancelText?: string;
    promptValue?: string;
  }>({
    type: 'alert',
    title: '',
    message: '',
    confirmText: 'OK',
    cancelText: 'Cancel'
  });
  const [promptInput, setPromptInput] = useState('');

  // Load all drafts from localStorage
  const loadDrafts = () => {
    const draftsJson = localStorage.getItem('deploymentDrafts');
    if (draftsJson) {
      try {
        const drafts = JSON.parse(draftsJson);
        setSavedDrafts(Array.isArray(drafts) ? drafts : []);
      } catch (err) {
        console.error('Error loading drafts:', err);
        setSavedDrafts([]);
      }
    }
  };

  // Load drafts on mount
  React.useEffect(() => {
    loadDrafts();
  }, []);

  const handleSaveDraft = () => {
    const defaultName = `Draft ${new Date().toLocaleString('en-IN', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`;
    
    setModalConfig({
      type: 'prompt',
      title: 'Save Draft',
      message: 'Enter a name for this draft (optional):',
      promptValue: defaultName,
      confirmText: 'Save',
      cancelText: 'Cancel',
      onConfirm: () => {
        const draftName = promptInput || defaultName;
        
        const draft = {
          id: Date.now().toString(),
          name: draftName,
          formData,
          requestType,
          dbScriptFiles,
          moduleBranches,
          savedAt: new Date().toISOString()
        };
        
        const draftsJson = localStorage.getItem('deploymentDrafts');
        let drafts = [];
        if (draftsJson) {
          try {
            drafts = JSON.parse(draftsJson);
            if (!Array.isArray(drafts)) drafts = [];
          } catch (err) {
            drafts = [];
          }
        }
        
        drafts.push(draft);
        localStorage.setItem('deploymentDrafts', JSON.stringify(drafts));
        loadDrafts();
        
        setModalConfig({
          type: 'alert',
          title: 'Success',
          message: `Draft "${draftName}" saved successfully!`,
          confirmText: 'OK',
          onConfirm: () => setShowModal(false)
        });
      },
      onCancel: () => setShowModal(false)
    });
    setPromptInput(defaultName);
    setShowModal(true);
  };

  const handleLoadDraft = (draft: any) => {
    setModalConfig({
      type: 'confirm',
      title: 'Load Draft',
      message: `Load draft "${draft.name}"? Current form data will be replaced.`,
      confirmText: 'Load',
      cancelText: 'Cancel',
      onConfirm: () => {
        setFormData(draft.formData);
        setRequestType(draft.requestType || 'tl_to_pm');
        setDbScriptFiles(draft.dbScriptFiles || []);
        setModuleBranches(draft.moduleBranches || {});
        setShowDrafts(false);
        setShowModal(false);
        
        // Show success message
        setTimeout(() => {
          setModalConfig({
            type: 'alert',
            title: 'Success',
            message: 'Draft loaded successfully!',
            confirmText: 'OK',
            onConfirm: () => setShowModal(false)
          });
          setShowModal(true);
        }, 100);
      },
      onCancel: () => setShowModal(false)
    });
    setShowModal(true);
  };

  const handleDeleteDraft = (draftId: string) => {
    setModalConfig({
      type: 'confirm',
      title: 'Delete Draft',
      message: 'Are you sure you want to delete this draft? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: () => {
        const draftsJson = localStorage.getItem('deploymentDrafts');
        if (draftsJson) {
          try {
            let drafts = JSON.parse(draftsJson);
            drafts = drafts.filter((d: any) => d.id !== draftId);
            localStorage.setItem('deploymentDrafts', JSON.stringify(drafts));
            loadDrafts();
          } catch (err) {
            console.error('Error deleting draft:', err);
          }
        }
        setShowModal(false);
      },
      onCancel: () => setShowModal(false)
    });
    setShowModal(true);
  };

  const handleClearForm = () => {
    setModalConfig({
      type: 'confirm',
      title: 'Clear Form',
      message: 'Are you sure you want to clear the entire form? All unsaved data will be lost.',
      confirmText: 'Clear',
      cancelText: 'Cancel',
      onConfirm: () => {
        setFormData({
          title: '',
          description: '',
          priority: 'medium',
          deploymentType: 'feature',
          modules: [],
          institutes: [],
          tls: [],
          pms: [],
          devopsAssignees: [],
          scheduledDate: '',
          dbScript: '',
          dbScriptFileName: '',
          testers: [],
          notifyTeam: true,
        });
        setRequestType('tl_to_pm');
        setDbScriptFiles([]);
        setModuleBranches({});
        setShowModal(false);

        // Show success message
        setTimeout(() => {
          setModalConfig({
            type: 'alert',
            title: 'Success',
            message: 'Form cleared successfully!',
            confirmText: 'OK',
            onConfirm: () => setShowModal(false)
          });
          setShowModal(true);
        }, 100);
      },
      onCancel: () => setShowModal(false)
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    // Validation logic by role
    if (
      !formData.title ||
      !formData.description ||
      formData.modules.length === 0 ||
      formData.institutes.length === 0 ||
      formData.scheduledDate === '' ||
      (currentUser.role === 'developer' && formData.tls.length === 0) ||
      (currentUser.role === 'developer' && formData.pms.length === 0) ||
      (currentUser.role === 'tl' && formData.pms.length === 0)
    ) {
      setModalConfig({
        type: 'alert',
        title: 'Validation Error',
        message: 'Please fill in all required fields before submitting the form.',
        confirmText: 'OK',
        onConfirm: () => setShowModal(false)
      });
      setShowModal(true);
      return;
    }

    // This is the single source of truth for the initial status — App.tsx
    // used to independently re-derive this from currentUser.role alone
    // (duplicating this logic, and doing it without the self-approval
    // records added above), which is why it's no longer done there.
    // A PM submitting self-certifies both tiers (no one reviews above PM);
    // a TL submitting self-certifies only the TL tier and still needs real
    // PM review; anyone else needs the full TL+PM review.
    let status: any = 'pending';
    if (currentUser.role === 'pm') {
      status = 'pm_approved';
    } else if (currentUser.role === 'tl') {
      status = 'tl_approved';
    }

    // Map selected module/institute IDs to full objects (with _id and name),
    // carrying along the optional manually-entered branch name for each module
    const selectedModules = formData.modules.map(sel => {
      const found = modules.find(m => m._id === sel || m.id === sel || m.name === sel);
      const branch = (moduleBranches[sel] || '').trim();
      return found
        ? { _id: found._id || found.id || found.name, name: found.name, branch }
        : { _id: sel, name: sel, branch };
    });
    const selectedInstitutes = formData.institutes.map(sel => {
      const found = institutes.find(i => i._id === sel || i.id === sel || i.name === sel);
      return found ? { _id: found._id || found.id || found.name, name: found.name } : { _id: sel, name: sel };
    });

    const newRequest = {
      id: Date.now().toString(),
      ...formData,
      requestedBy: currentUser,
      requestedAt: new Date().toISOString(),
      status,
      institutes: selectedInstitutes,
      modules: selectedModules,
      tlApprovals: formData.tls.map(tlId => {
        const tlUser = tls.find(t => t.id === tlId);
        // Auto-approve if the TL is creating their own request
        // Compare both id and email for better matching
        const isCurrentUserTL = currentUser.role === 'tl' && 
          (currentUser.id === tlId || 
           currentUser.email === tlUser?.email ||
           currentUser.username === tlUser?.username);
        
        return {
          tl: tlUser || { id: tlId, name: tlId, email: '', role: 'tl' },
          approved: isCurrentUserTL, // Auto-approve for current TL
          approvedAt: isCurrentUserTL ? new Date().toISOString() : ''
        };
      }).concat(
        // If current user is TL and not in the list, add them with auto-approval
        currentUser.role === 'tl' && !formData.tls.includes(currentUser.id)
          ? [{
              tl: currentUser,
              approved: true,
              approvedAt: new Date().toISOString()
            }]
          : []
      ),
      pmApprovals: formData.pms.map(pmId => {
        const pmUser = pms.find(p => p.id === pmId);
        // Auto-approve if the PM is creating their own request
        // Compare both id and email for better matching
        const isCurrentUserPM = currentUser.role === 'pm' && 
          (currentUser.id === pmId || 
           currentUser.email === pmUser?.email ||
           currentUser.username === pmUser?.username);
        
        return {
          pm: pmUser || { id: pmId, name: pmId, email: '', role: 'pm' },
          approved: isCurrentUserPM, // Auto-approve for current PM
          approvedAt: isCurrentUserPM ? new Date().toISOString() : ''
        };
      }).concat(
        // A PM submitting their own request self-certifies the PM tier —
        // mirroring the TL self-approval below — so this must always be a
        // real, recorded approval entry, not just an unbacked status flag.
        // Without this, a PM's request could reach `pm_approved` with a
        // completely empty pmApprovals array: nothing on record shows who
        // actually approved it.
        currentUser.role === 'pm' && !formData.pms.includes(currentUser.id)
          ? [{
              pm: currentUser,
              approved: true,
              approvedAt: new Date().toISOString()
            }]
          : []
      ),
      devopsAssignees: formData.devopsAssignees.map(devopsId => {
        const devopsUser = devopsUsers.find(d => d.id === devopsId);
        return devopsUser || { id: devopsId, name: devopsId, email: '', role: 'devops' };
      }),
      testers: formData.testers.map(testerId => {
        const testerUser = testers.find(t => t.id === testerId);
        return testerUser ? {
          id: testerUser.id,
          name: testerUser.name || testerUser.username || testerUser.email || testerId,
          email: testerUser.email || '',
          username: testerUser.username || ''
        } : { id: testerId, name: testerId, email: '', username: '' };
      }),
    };

    // Mirrors the backend: a request still waiting on TLs needs a TL approver,
    // and one not yet PM-approved needs a PM approver. A PM's own request is
    // already PM-approved, so it needs neither.
    const needsTl = status === 'pending' && newRequest.tlApprovals.length === 0;
    const needsPm = status !== 'pm_approved' && newRequest.pmApprovals.length === 0;
    if (needsTl || needsPm) {
      setModalConfig({
        type: 'alert',
        title: 'Validation Error',
        message: needsTl && needsPm
          ? 'Please select at least one Team Lead (TL) and one Program Manager (PM) approver.'
          : needsTl
            ? 'Please select at least one Team Lead (TL) approver.'
            : 'Please select at least one Program Manager (PM) approver.',
        confirmText: 'OK',
        onConfirm: () => setShowModal(false)
      });
      setShowModal(true);
      return;
    }
    // Derive the starting status from what's actually recorded as approved,
    // so a PM who also listed TLs still waits for those TLs.
    const allTl = newRequest.tlApprovals.every((a: any) => a.approved);
    const allPm = newRequest.pmApprovals.every((a: any) => a.approved);
    newRequest.status = !allTl ? 'pending' : !allPm ? 'tl_approved' : 'pm_approved';

    setSubmitting(true);
    try {
      await onSubmit(newRequest);
    } catch {
      // The caller already toasted the server's error. Keep every field as
      // entered so the user can fix the problem and resubmit.
      return;
    } finally {
      setSubmitting(false);
    }

    // Don't clear drafts after submission (user may want to keep them)
    // User can manually delete drafts if needed

    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      deploymentType: 'feature',
      modules: [],
      institutes: [],
      tls: [],
      pms: [],
      devopsAssignees: [],
      scheduledDate: '',
      dbScript: '',
      dbScriptFileName: '',
      testers: [],
      notifyTeam: true,
    });
    setDbScriptFiles([]);
    setModuleBranches({});
    setRequestType('tl_to_pm');
  };

  if (currentUser.role !== 'developer' && currentUser.role !== 'tl' && currentUser.role !== 'pm') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-gray-500">
          Only Developer, Team Lead (TL), or Program Manager (PM) can create deployment requests.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Plus className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            {currentUser.role === 'developer' && (
              <>
                <h1 className="text-xl font-semibold text-gray-900">Developer: Submit Deployment Request</h1>
                <p className="text-gray-600">Create a new deployment request for TL/PM approval</p>
              </>
            )}
            {currentUser.role === 'tl' && (
              <>
                <h1 className="text-xl font-semibold text-gray-900">Team Lead (TL): Submit Deployment Request</h1>
                <p className="text-gray-600">Create a deployment request for PM approval</p>
              </>
            )}
            {currentUser.role === 'pm' && (
              <>
                <h1 className="text-xl font-semibold text-gray-900">Program Manager (PM): Submit Deployment Request</h1>
                <p className="text-gray-600">Create a deployment request for DevOps or TL/PM workflow</p>
              </>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {currentUser.role === 'pm' && (
            <div>
              <label htmlFor="requestType" className="block text-sm font-medium text-gray-700 mb-2">
                Request Type *
              </label>
              <select
                id="requestType"
                value={requestType}
                onChange={e => setRequestType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="pm_to_devops">PM to DevOps (Direct Deploy)</option>
              </select>
            </div>
          )}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="w-4 h-4 inline mr-2" />
              Request Title *
            </label>
            <input
              type="text"
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Brief description of the deployment"
              required
            />
          </div>

          <div>
            <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-2">
              Priority Level *
            </label>
            <select
              id="priority"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value as 'low' | 'medium' | 'high' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="high">🔴 High - Deploy as soon as possible</option>
              <option value="medium">🟡 Medium - Normal priority</option>
              <option value="low">🟢 Low - Can be deployed anytime</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Select priority to help DevOps team prioritize deployments</p>
          </div>

          {/* Divider for visual separation */}
          <div className="col-span-2 border-t border-gray-200 my-2"></div>

          <div>
            <label htmlFor="deploymentType" className="block text-sm font-medium text-gray-700 mb-2">
              Deployment Type *
            </label>
            <select
              id="deploymentType"
              value={formData.deploymentType}
              onChange={(e) => setFormData({ ...formData, deploymentType: e.target.value as 'bug' | 'feature' | 'payload' | 'version' | 'new-deployment' | 'db-script' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="bug">🐛 Bug Fix</option>
              <option value="feature">✨ Feature</option>
              <option value="payload">📦 Payload</option>
              <option value="version">🔢 Version Update</option>
              <option value="new-deployment">🚀 New Deployment</option>
              <option value="db-script">🗄️ DB Script</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Select the type of deployment</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Package className="w-4 h-4 inline mr-2" />
              Modules *
            </label>
            <button
              type="button"
              onClick={() => setShowModuleModal(true)}
              disabled={modulesLoading}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-left hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {modulesLoading ? (
                <span className="text-gray-400">Loading modules...</span>
              ) : formData.modules.length > 0 ? (
                <span className="text-gray-900 font-medium">
                  {formData.modules.length} module{formData.modules.length !== 1 ? 's' : ''} selected
                </span>
              ) : (
                <span className="text-gray-400">Click to select modules...</span>
              )}
            </button>
            {formData.modules.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {modules
                  .filter(module => formData.modules.includes(module._id || module.id || module.name))
                  .map(module => {
                    const modId = module._id || module.id || module.name;
                    return (
                      <span
                        key={modId}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-700 border border-blue-300"
                      >
                        {module.name}
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              modules: formData.modules.filter(id => id !== modId)
                            });
                          }}
                          className="ml-1 hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                          title="Remove module"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    );
                  })}
              </div>
            )}
            {formData.modules.length > 0 && (
              <div className="mt-3 space-y-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-xs font-medium text-gray-500">
                  Branch name <span className="text-gray-400">(optional, per module)</span>
                </p>
                {modules
                  .filter(module => formData.modules.includes(module._id || module.id || module.name))
                  .map(module => {
                    const moduleId = module._id || module.id || module.name;
                    return (
                      <div key={moduleId} className="flex items-center gap-2">
                        <span className="w-40 flex-shrink-0 truncate text-sm text-gray-700 font-medium">
                          {module.name}
                        </span>
                        <input
                          type="text"
                          value={moduleBranches[moduleId] || ''}
                          onChange={(e) =>
                            setModuleBranches(prev => ({ ...prev, [moduleId]: e.target.value }))
                          }
                          placeholder="e.g. feature/login-fix"
                          className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Institutes *
            </label>
            <button
              type="button"
              onClick={() => setShowInstituteModal(true)}
              disabled={institutesLoading}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-left hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {institutesLoading ? (
                <span className="text-gray-400">Loading institutes...</span>
              ) : formData.institutes.length > 0 ? (
                <span className="text-gray-900 font-medium">
                  {formData.institutes.length} institute{formData.institutes.length !== 1 ? 's' : ''} selected
                </span>
              ) : (
                <span className="text-gray-400">Click to select institutes...</span>
              )}
            </button>
            {formData.institutes.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {institutes
                  .filter(inst => formData.institutes.includes(inst._id || inst.id || inst.name))
                  .map(inst => {
                    const instId = inst._id || inst.id || inst.name;
                    return (
                      <span
                        key={instId}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-700 border border-blue-300"
                      >
                        {inst.name}
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              institutes: formData.institutes.filter(id => id !== instId)
                            });
                          }}
                          className="ml-1 hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                          title="Remove institute"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    );
                  })}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-2" />
              Date & Time *
            </label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label htmlFor="scheduledDate" className="block text-xs text-gray-600 mb-1">Date</label>
                <input
                  type="date"
                  id="scheduledDate"
                  value={formData.scheduledDate.split('T')[0] || ''}
                  onChange={(e) => {
                    const date = e.target.value;
                    const time = formData.scheduledDate.split('T')[1] || '00:00';
                    setFormData({ ...formData, scheduledDate: `${date}T${time}` });
                  }}
                  min={todayLocal()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white cursor-pointer"
                  required
                />
              </div>
              <div>
                <label htmlFor="scheduledTime" className="block text-xs text-gray-600 mb-1">Time</label>
                <div className="flex gap-1">
                  {/* Hour selector (1-12) */}
                  <select
                    value={(() => {
                      const time = formData.scheduledDate.split('T')[1] || '00:00';
                      const [hours] = time.split(':');
                      const hour24 = parseInt(hours);
                      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
                      return hour12.toString().padStart(2, '0');
                    })()}
                    onChange={(e) => {
                      const date = formData.scheduledDate.split('T')[0] || todayLocal();
                      const time = formData.scheduledDate.split('T')[1] || '00:00';
                      const [, minutes] = time.split(':');
                      const hour12 = parseInt(e.target.value);
                      const isPM = parseInt(time.split(':')[0]) >= 12;
                      const hour24 = isPM ? (hour12 === 12 ? 12 : hour12 + 12) : (hour12 === 12 ? 0 : hour12);
                      setFormData({ ...formData, scheduledDate: `${date}T${hour24.toString().padStart(2, '0')}:${minutes}` });
                    }}
                    className="flex-1 px-2 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                      <option key={h} value={h.toString().padStart(2, '0')}>{h.toString().padStart(2, '0')}</option>
                    ))}
                  </select>
                  <span className="flex items-center text-gray-500 font-bold">:</span>
                  {/* Minute selector (00-59) */}
                  <select
                    value={(() => {
                      const time = formData.scheduledDate.split('T')[1] || '00:00';
                      const [, minutes] = time.split(':');
                      return minutes;
                    })()}
                    onChange={(e) => {
                      const date = formData.scheduledDate.split('T')[0] || todayLocal();
                      const time = formData.scheduledDate.split('T')[1] || '00:00';
                      const [hours] = time.split(':');
                      setFormData({ ...formData, scheduledDate: `${date}T${hours}:${e.target.value}` });
                    }}
                    className="flex-1 px-2 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    {Array.from({ length: 60 }, (_, i) => i).map(m => (
                      <option key={m} value={m.toString().padStart(2, '0')}>{m.toString().padStart(2, '0')}</option>
                    ))}
                  </select>
                  {/* AM/PM selector */}
                  <select
                    value={(() => {
                      const time = formData.scheduledDate.split('T')[1] || '00:00';
                      const [hours] = time.split(':');
                      return parseInt(hours) >= 12 ? 'PM' : 'AM';
                    })()}
                    onChange={(e) => {
                      const date = formData.scheduledDate.split('T')[0] || todayLocal();
                      const time = formData.scheduledDate.split('T')[1] || '00:00';
                      const [hours, minutes] = time.split(':');
                      const hour24 = parseInt(hours);
                      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
                      const newHour24 = e.target.value === 'PM' 
                        ? (hour12 === 12 ? 12 : hour12 + 12) 
                        : (hour12 === 12 ? 0 : hour12);
                      setFormData({ ...formData, scheduledDate: `${date}T${newHour24.toString().padStart(2, '0')}:${minutes}` });
                    }}
                    className="px-2 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-medium"
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">Select deployment date and time</p>
          </div>

          {/* Conflict Check — fully built backend + component, previously never
              wired into any screen. Shows live if the selected modules already
              have a deployment scheduled for the same date. */}
          {formData.modules.length > 0 && formData.scheduledDate && (
            <ConflictCheck
              moduleIds={formData.modules}
              scheduledDate={formData.scheduledDate}
            />
          )}

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Detailed description of what will be deployed and why..."
              required
            />
          </div>

          {/* DB Script Field - Optional with File Upload */}
          <div>
            <label htmlFor="dbScript" className="block text-sm font-medium text-gray-700 mb-2">
              DB Script <span className="text-xs text-gray-500">(Optional - For DevOps Team)</span>
            </label>
            
            {/* Tab-like Toggle */}
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                onClick={async () => {
                  setDbScriptFiles([]);
                  // Try to paste from clipboard
                  try {
                    const text = await navigator.clipboard.readText();
                    if (text) {
                      setFormData({ ...formData, dbScript: text });
                    }
                  } catch {
                    // Clipboard access denied or empty — nothing to paste
                  }
                }}
                className={`px-4 py-2 text-sm font-medium rounded-lg ${dbScriptFiles.length === 0 ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                📝 Paste Code
              </button>
              <button
                type="button"
                onClick={() => document.getElementById('dbScriptFile')?.click()}
                className={`px-4 py-2 text-sm font-medium rounded-lg ${dbScriptFiles.length > 0 ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                📁 Upload File{dbScriptFiles.length > 0 ? 's' : ''}
              </button>
            </div>

            {/* File Input (Hidden) - Multiple Files */}
            <input
              type="file"
              id="dbScriptFile"
              accept=".sql,.txt,.sh,.py,.js"
              className="hidden"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length > 0) {
                  const newFiles: Array<{name: string, content: string}> = [];
                  let filesProcessed = 0;
                  
                  files.forEach(file => {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      const content = event.target?.result as string;
                      newFiles.push({ name: file.name, content });
                      filesProcessed++;
                      
                      if (filesProcessed === files.length) {
                        setDbScriptFiles(prev => [...prev, ...newFiles]);
                        // Combine all file contents
                        const combinedContent = newFiles.map(f => `/* File: ${f.name} */\n${f.content}`).join('\n\n');
                        setFormData(prev => ({ 
                          ...prev, 
                          dbScript: prev.dbScript ? `${prev.dbScript}\n\n${combinedContent}` : combinedContent,
                          dbScriptFileName: newFiles.map(f => f.name).join(', ')
                        }));
                      }
                    };
                    reader.readAsText(file);
                  });
                }
              }}
            />

            {/* Show uploaded files */}
            {dbScriptFiles.length > 0 && (
              <div className="mb-2 space-y-2">
                {dbScriptFiles.map((file, index) => (
                  <div key={index} className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                    <span className="text-sm text-green-700">
                      📄 {file.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const newFiles = dbScriptFiles.filter((_, i) => i !== index);
                        setDbScriptFiles(newFiles);
                        if (newFiles.length === 0) {
                          setFormData({ ...formData, dbScript: '', dbScriptFileName: '' });
                        } else {
                          const combinedContent = newFiles.map(f => `/* File: ${f.name} */\n${f.content}`).join('\n\n');
                          setFormData({ 
                            ...formData, 
                            dbScript: combinedContent,
                            dbScriptFileName: newFiles.map(f => f.name).join(', ')
                          });
                        }
                      }}
                      className="text-red-500 hover:text-red-700 text-sm font-medium"
                    >
                      ✕ Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Textarea for pasting code */}
            {dbScriptFiles.length === 0 && (
              <textarea
                id="dbScript"
                value={formData.dbScript}
                onChange={(e) => setFormData({ ...formData, dbScript: e.target.value })}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                placeholder="Paste your database script here (MongoDB, migration, etc.)&#10;&#10;Example:&#10;db.user.find().sort({age: 1});&#10;db.user.createIndex({email: 1}, {unique: true});"
              />
            )}
            
            <p className="text-xs text-gray-500 mt-1">
              {dbScriptFiles.length > 0
                ? `${dbScriptFiles.length} file${dbScriptFiles.length > 1 ? 's' : ''} uploaded successfully. DevOps team will be able to download them.`
                : 'Paste code or upload multiple files. DevOps team can copy and use this during deployment.'
              }
            </p>
          </div>

          {/* Testers Field - Optional Multi-Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Testers <span className="text-xs text-gray-500">(Optional - Select testers for this deployment)</span>
            </label>
            <Select
              isMulti
              options={testers.map(tester => ({ value: tester.id, label: tester.username || tester.name }))}
              value={Array.isArray(formData.testers) && formData.testers.length > 0
                ? testers.filter(t => formData.testers.includes(t.id)).map(t => ({ value: t.id, label: t.username || t.name }))
                : []}
              onChange={selected => {
                setFormData({ ...formData, testers: selected.map((opt: any) => opt.value) });
              }}
              classNamePrefix="react-select"
              placeholder="Select testers (optional)..."
              isClearable
            />
            <p className="text-xs text-gray-500 mt-1">Select QA/testers who should be involved in testing this deployment</p>
          </div>

          {currentUser.role === 'developer' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Leads (TLs) to Approve *
                </label>
                <Select
                  isMulti
                  options={tls.map(tl => ({ value: tl.id, label: tl.username || tl.name }))}
                  value={Array.isArray(formData.tls) && formData.tls.length > 0
                    ? tls.filter(tl => formData.tls.includes(tl.id)).map(tl => ({ value: tl.id, label: tl.username || tl.name }))
                    : []}
                  onChange={selected => {
                    setFormData({ ...formData, tls: selected.map((opt: any) => opt.value) });
                  }}
                  classNamePrefix="react-select"
                  placeholder="Select TLs..."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Program Managers (PMs) to Approve *
                </label>
                <Select
                  isMulti
                  options={pms.map(pm => ({ value: pm.id, label: pm.username || pm.name }))}
                  value={Array.isArray(formData.pms) && formData.pms.length > 0
                    ? pms.filter(pm => formData.pms.includes(pm.id)).map(pm => ({ value: pm.id, label: pm.username || pm.name }))
                    : []}
                  onChange={selected => {
                    setFormData({ ...formData, pms: selected.map((opt: any) => opt.value) });
                  }}
                  classNamePrefix="react-select"
                  placeholder="Select PMs..."
                  required
                />
              </div>
            </>
          )}
          {currentUser.role === 'tl' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Program Managers (PMs) to Approve *
              </label>
              <Select
                isMulti
                options={pms.map(pm => ({ value: pm.id, label: pm.username || pm.name }))}
                value={pms.filter(pm => formData.pms.includes(pm.id)).map(pm => ({ value: pm.id, label: pm.username || pm.name }))}
                onChange={selected => {
                  setFormData({ ...formData, pms: selected.map((opt: any) => opt.value) });
                }}
                classNamePrefix="react-select"
                placeholder="Select PMs..."
                required
              />
            </div>
          )}
          
          {/* DevOps Assignees Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              DevOps Team Members to Assign
            </label>
            <Select
              isMulti
              options={devopsUsers.map(devops => ({ value: devops.id, label: devops.username || devops.name }))}
              value={devopsUsers.filter(devops => formData.devopsAssignees.includes(devops.id)).map(devops => ({ value: devops.id, label: devops.username || devops.name }))}
              onChange={selected => {
                setFormData({ ...formData, devopsAssignees: selected.map((opt: any) => opt.value) });
              }}
              classNamePrefix="react-select"
              placeholder="Select DevOps team members (optional, any one can deploy)..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Select multiple DevOps members. Any assigned member can complete the deployment.
            </p>
          </div>
          
          {/* Notify Team Members Checkbox */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                checked={formData.notifyTeam}
                onChange={(e) => setFormData({ ...formData, notifyTeam: e.target.checked })}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <div className="ml-3">
                <span className="text-sm font-medium text-gray-900">
                  Notify other team members from the same Module(s)
                </span>
                <p className="text-xs text-gray-600 mt-1">
                  When enabled, all developers working in the selected Modules will be notified about this deployment request.
                  This helps keep your entire team informed about deployments affecting shared modules.
                </p>
              </div>
            </label>
          </div>
          
          {(currentUser.role !== 'pm') && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Approval Process</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center">
                  <div className="w-2 h-2 rounded-full mr-3"></div>
                  <span>1. Team Lead (TL) Approval</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 rounded-full mr-3"></div>
                  <span>2. Program Manager (PM) Approval</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 rounded-full mr-3"></div>
                  <span>3. DevOps Deployment</span>
                </div>
              </div>
            </div>
          )}

          {/* Saved Drafts Section */}
          {savedDrafts.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-blue-900 flex items-center">
                  <FileStack className="w-4 h-4 mr-2" />
                  Saved Drafts ({savedDrafts.length})
                </h3>
                <button
                  type="button"
                  onClick={() => setShowDrafts(!showDrafts)}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  {showDrafts ? 'Hide' : 'Show'}
                </button>
              </div>
              
              {showDrafts && (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {savedDrafts.map((draft) => (
                    <div
                      key={draft.id}
                      className="bg-white border border-gray-200 rounded-lg p-3 flex items-center justify-between hover:shadow-sm transition-shadow"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{draft.name}</p>
                        <p className="text-xs text-gray-500">
                          Saved: {new Date(draft.savedAt).toLocaleString('en-IN', {
                            timeZone: 'Asia/Kolkata',
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }).replace(',', '')}
                        </p>
                        {draft.formData.title && (
                          <p className="text-xs text-gray-600 mt-1 truncate">Title: {draft.formData.title}</p>
                        )}
                      </div>
                      <div className="flex space-x-2 ml-3">
                        <button
                          type="button"
                          onClick={() => handleLoadDraft(draft)}
                          className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        >
                          Load
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteDraft(draft.id)}
                          className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors flex items-center"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-between items-center">
            <button
              type="button"
              onClick={handleClearForm}
              className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors flex items-center"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Clear Form
            </button>
            
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={handleSaveDraft}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Draft
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting…' : 'Create Request'}
              </button>
            </div>
          </div>
        </form>
      </div>
      
      {/* Institute Selection Modal */}
      <InstituteSelectionModal
        isOpen={showInstituteModal}
        onClose={() => setShowInstituteModal(false)}
        institutes={institutes}
        selectedInstitutes={formData.institutes}
        onConfirm={(selected) => setFormData({ ...formData, institutes: selected })}
      />

      {/* Module Selection Modal */}
      <ModuleSelectionModal
        isOpen={showModuleModal}
        onClose={() => setShowModuleModal(false)}
        modules={modules}
        selectedModules={formData.modules}
        onConfirm={(selected) => setFormData({ ...formData, modules: selected })}
      />

      {/* Custom Confirmation/Alert Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full animate-fade-in">
            {/* Modal Header */}
            <div className={`px-6 py-4 border-b border-gray-200 flex items-center justify-between ${
              modalConfig.type === 'confirm' && modalConfig.title === 'Delete Draft' ? 'bg-red-50' :
              modalConfig.type === 'alert' && modalConfig.title === 'Success' ? 'bg-green-50' :
              modalConfig.type === 'alert' && modalConfig.title === 'Validation Error' ? 'bg-red-50' :
              'bg-blue-50'
            }`}>
              <div className="flex items-center gap-3">
                {modalConfig.type === 'alert' && modalConfig.title === 'Success' && (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                )}
                {modalConfig.type === 'alert' && modalConfig.title === 'Validation Error' && (
                  <AlertCircle className="w-6 h-6 text-red-600" />
                )}
                {modalConfig.type === 'confirm' && modalConfig.title === 'Delete Draft' && (
                  <AlertCircle className="w-6 h-6 text-red-600" />
                )}
                {modalConfig.type === 'confirm' && modalConfig.title !== 'Delete Draft' && (
                  <AlertCircle className="w-6 h-6 text-blue-600" />
                )}
                {modalConfig.type === 'prompt' && (
                  <FileText className="w-6 h-6 text-blue-600" />
                )}
                <h3 className="text-lg font-semibold text-gray-900">{modalConfig.title}</h3>
              </div>
              <button
                onClick={() => {
                  if (modalConfig.onCancel) {
                    modalConfig.onCancel();
                  } else {
                    setShowModal(false);
                  }
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4">
              <p className="text-gray-700 leading-relaxed">{modalConfig.message}</p>
              
              {modalConfig.type === 'prompt' && (
                <input
                  type="text"
                  value={promptInput}
                  onChange={(e) => setPromptInput(e.target.value)}
                  className="w-full mt-4 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter name..."
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && modalConfig.onConfirm) {
                      modalConfig.onConfirm();
                    }
                  }}
                />
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 rounded-b-lg">
              {modalConfig.type !== 'alert' && (
                <button
                  onClick={() => {
                    if (modalConfig.onCancel) {
                      modalConfig.onCancel();
                    } else {
                      setShowModal(false);
                    }
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  {modalConfig.cancelText || 'Cancel'}
                </button>
              )}
              <button
                onClick={() => {
                  if (modalConfig.onConfirm) {
                    modalConfig.onConfirm();
                  } else {
                    setShowModal(false);
                  }
                }}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  modalConfig.title === 'Delete Draft' || modalConfig.title === 'Clear Form'
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : modalConfig.title === 'Success'
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {modalConfig.confirmText || 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmitRequestForm;
